import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { RefreshCw, AlertTriangle, CheckCircle2, ShieldAlert, History, Play } from 'lucide-react';
import ManualWalletEntryDialog from './ManualWalletEntryDialog';

const API = process.env.REACT_APP_BACKEND_URL;

const WALLET_LABEL = {
  ott: 'OTT Wallet',
  iptv: 'IPTV Wallet',
  railtel: 'Railtel Wallet',
};
const WALLET_COLOR = {
  ott: 'text-indigo-700 bg-indigo-50 border-indigo-200',
  iptv: 'text-amber-700 bg-amber-50 border-amber-200',
  railtel: 'text-teal-700 bg-teal-50 border-teal-200',
};

const fmt = (n) => `Rs.${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function WalletReconciliation() {
  const { token } = useAuth();
  const [scanning, setScanning] = useState(false);
  const [scan, setScan] = useState(null);
  const [confirmFix, setConfirmFix] = useState(null); // { wallet_type, report }
  const [fixing, setFixing] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const runScan = useCallback(async () => {
    setScanning(true);
    try {
      const res = await fetch(`${API}/api/admin/wallet-reconcile/scan`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Scan failed');
        return;
      }
      const data = await res.json();
      setScan(data);
      if (data.grand_total_over_credit > 0) {
        toast.warning(`Found ${fmt(data.grand_total_over_credit)} of over-credited wallets`);
      } else {
        toast.success('All wallets reconciled — no drift detected');
      }
    } catch (e) {
      toast.error('Scan failed: ' + e.message);
    } finally {
      setScanning(false);
    }
  }, [token]);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/wallet-reconcile/history?limit=100`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setHistory(data.history || []);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setHistoryLoading(false);
    }
  }, [token]);

  useEffect(() => {
    runScan();
    loadHistory();
  }, [runScan, loadHistory]);

  const applyFix = async () => {
    if (!confirmFix) return;
    setFixing(true);
    try {
      const res = await fetch(`${API}/api/admin/wallet-reconcile/fix`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ wallet_type: confirmFix.wallet_type, confirm: true }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Fix failed');
        return;
      }
      const data = await res.json();
      toast.success(`Fixed ${data.corrections_applied} wallet(s), recovered ${fmt(data.total_amount_removed)}`);
      setConfirmFix(null);
      await runScan();
      await loadHistory();
    } catch (e) {
      toast.error('Fix failed: ' + e.message);
    } finally {
      setFixing(false);
    }
  };

  const grandTotal = scan?.grand_total_over_credit || 0;

  return (
    <div className="space-y-4" data-testid="wallet-reconcile-page">
      {/* Header + Scan */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div>
          <h2 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
            <ShieldAlert className="h-5 w-5 text-amber-600" /> Wallet Reconciliation
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            Compares each wallet balance against its ledger of successful credits/debits.
            Any drift is flagged as over-credited (double-crediting bug) or under-credited (manual adjustment).
          </p>
        </div>
        <div className="flex items-center gap-2">
          <ManualWalletEntryDialog token={token} onDone={() => { runScan(); loadHistory(); }} />
          <Button
            variant="outline"
            onClick={runScan}
            disabled={scanning}
            data-testid="run-scan-btn"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${scanning ? 'animate-spin' : ''}`} />
            {scanning ? 'Scanning…' : 'Re-scan'}
          </Button>
        </div>
      </div>

      {/* Summary card */}
      {scan && (
        <Card className={grandTotal > 0 ? 'border-amber-300' : 'border-emerald-300'}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm text-slate-500">Total over-credited across all wallets</div>
                <div className={`text-3xl font-bold ${grandTotal > 0 ? 'text-amber-700' : 'text-emerald-700'}`}
                     data-testid="grand-total-over-credit">
                  {fmt(grandTotal)}
                </div>
                <div className="text-xs text-slate-400 mt-1">
                  Last scanned: {new Date(scan.scanned_at).toLocaleString()}
                </div>
              </div>
              {grandTotal === 0 && (
                <Badge className="bg-emerald-100 text-emerald-700 border border-emerald-200">
                  <CheckCircle2 className="h-4 w-4 mr-1" /> All Clean
                </Badge>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Per-wallet cards */}
      {scan && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {scan.reports.map((r) => (
            <Card key={r.wallet_type} className={r.over_credited_count > 0 ? 'border-amber-300' : ''}>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">
                    <Badge variant="outline" className={WALLET_COLOR[r.wallet_type]}>
                      {WALLET_LABEL[r.wallet_type]}
                    </Badge>
                  </CardTitle>
                  {r.over_credited_count > 0 && (
                    <Button
                      size="sm"
                      className="bg-amber-600 hover:bg-amber-700"
                      onClick={() => setConfirmFix({ wallet_type: r.wallet_type, report: r })}
                      data-testid={`fix-btn-${r.wallet_type}`}
                    >
                      <Play className="h-3.5 w-3.5 mr-1" /> Fix
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-500">Over-credited</span>
                  <span className={`font-semibold ${r.over_credited_count > 0 ? 'text-amber-700' : ''}`}
                        data-testid={`over-count-${r.wallet_type}`}>
                    {r.over_credited_count}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Under-credited</span>
                  <span className={`font-semibold ${r.under_credited_count > 0 ? 'text-red-700' : ''}`}>
                    {r.under_credited_count}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Clean</span>
                  <span className="font-semibold text-emerald-700">{r.clean_count}</span>
                </div>
                <div className="flex justify-between border-t pt-2 mt-2">
                  <span className="text-slate-500">Over-credit amount</span>
                  <span className="font-semibold text-amber-700"
                        data-testid={`over-amount-${r.wallet_type}`}>
                    {fmt(r.total_over_credit_amount)}
                  </span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Drilldown tabs */}
      {scan && (
        <Tabs defaultValue="over" className="mt-4">
          <TabsList>
            <TabsTrigger value="over" data-testid="tab-over-credited">
              <AlertTriangle className="h-4 w-4 mr-1" /> Over-credited
            </TabsTrigger>
            <TabsTrigger value="under" data-testid="tab-under-credited">
              Under-credited
            </TabsTrigger>
            <TabsTrigger value="history" data-testid="tab-history">
              <History className="h-4 w-4 mr-1" /> Fix History
            </TabsTrigger>
          </TabsList>

          <TabsContent value="over">
            <DriftTable
              rows={scan.reports.flatMap((r) => r.over_credited.map((row) => ({ ...row, wallet_type: r.wallet_type })))}
              emptyText="No over-credited wallets — nothing to fix."
              showDrift
              testid="over-credit-table"
            />
          </TabsContent>

          <TabsContent value="under">
            <DriftTable
              rows={scan.reports.flatMap((r) => r.under_credited.map((row) => ({ ...row, wallet_type: r.wallet_type })))}
              emptyText="No under-credited wallets."
              showDrift
              danger
              testid="under-credit-table"
            />
            {scan.reports.some((r) => r.under_credited_count > 0) && (
              <p className="text-xs text-slate-500 mt-2">
                Under-credited wallets are never auto-adjusted — please review each one manually
                (usually caused by a legitimate manual debit).
              </p>
            )}
          </TabsContent>

          <TabsContent value="history">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Recent Corrections</CardTitle>
                <CardDescription>Audit trail of every reconciliation fix applied.</CardDescription>
              </CardHeader>
              <CardContent>
                {historyLoading ? (
                  <div className="flex justify-center py-6"><RefreshCw className="h-5 w-5 animate-spin text-slate-400" /></div>
                ) : history.length === 0 ? (
                  <p className="text-sm text-slate-500 py-6 text-center">No corrections applied yet.</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>When</TableHead>
                        <TableHead>Wallet</TableHead>
                        <TableHead>Customer</TableHead>
                        <TableHead className="text-right">Actual → After</TableHead>
                        <TableHead className="text-right">Removed</TableHead>
                        <TableHead>By</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {history.map((h, i) => (
                        <TableRow key={i}>
                          <TableCell className="text-xs">{new Date(h.created_at).toLocaleString()}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className={WALLET_COLOR[h.wallet_type]}>{WALLET_LABEL[h.wallet_type]}</Badge>
                          </TableCell>
                          <TableCell className="font-mono text-xs">
                            <div className="font-sans text-sm font-medium text-slate-900">{h.customer_name || h.customer_id}</div>
                            <div className="text-[10px] text-slate-500">{h.customer_id}</div>
                          </TableCell>
                          <TableCell className="text-right text-xs">
                            {fmt(h.actual_before)} → <span className="font-semibold">{fmt(h.balance_after)}</span>
                          </TableCell>
                          <TableCell className="text-right text-amber-700 font-semibold">−{fmt(h.drift_removed)}</TableCell>
                          <TableCell className="text-xs text-slate-500">{h.acted_by}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      )}

      {/* Confirm Fix Dialog */}
      <Dialog open={!!confirmFix} onOpenChange={(o) => !o && setConfirmFix(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Apply Reconciliation Fix?</DialogTitle>
            <DialogDescription>
              This will deduct the drift amount from every over-credited{' '}
              <strong>{confirmFix && WALLET_LABEL[confirmFix.wallet_type]}</strong>. Each correction is
              recorded in the audit log and cannot be undone.
            </DialogDescription>
          </DialogHeader>
          {confirmFix && (
            <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-sm space-y-1">
              <div className="flex justify-between">
                <span className="text-slate-600">Wallets to fix</span>
                <span className="font-semibold">{confirmFix.report.over_credited_count}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">Total amount to deduct</span>
                <span className="font-semibold text-amber-800">{fmt(confirmFix.report.total_over_credit_amount)}</span>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmFix(null)} disabled={fixing}>Cancel</Button>
            <Button
              className="bg-amber-600 hover:bg-amber-700"
              onClick={applyFix}
              disabled={fixing}
              data-testid="confirm-fix-btn"
            >
              {fixing ? <><RefreshCw className="h-4 w-4 mr-2 animate-spin" /> Fixing…</> : 'Apply Fix'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DriftTable({ rows, emptyText, showDrift, danger, testid }) {
  if (!rows || rows.length === 0) {
    return (
      <Card>
        <CardContent className="py-10 text-center text-sm text-slate-500">
          <CheckCircle2 className="h-8 w-8 mx-auto mb-2 text-emerald-500" />
          {emptyText}
        </CardContent>
      </Card>
    );
  }
  return (
    <Card>
      <CardContent className="pt-4">
        <Table data-testid={testid}>
          <TableHeader>
            <TableRow>
              <TableHead>Wallet</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead className="text-right">Actual Balance</TableHead>
              <TableHead className="text-right">Ledger Expects</TableHead>
              {showDrift && <TableHead className="text-right">Drift</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={`${r.wallet_type}-${r.customer_id}`}>
                <TableCell>
                  <Badge variant="outline" className={WALLET_COLOR[r.wallet_type]}>{WALLET_LABEL[r.wallet_type]}</Badge>
                </TableCell>
                <TableCell>
                  <div className="font-medium text-slate-900">{r.customer_name || r.customer_id}</div>
                  <div className="text-[10px] text-slate-500 font-mono">{r.customer_id}</div>
                </TableCell>
                <TableCell className="text-right">{fmt(r.actual_balance)}</TableCell>
                <TableCell className="text-right">{fmt(r.expected_balance)}</TableCell>
                {showDrift && (
                  <TableCell className={`text-right font-semibold ${danger ? 'text-red-700' : 'text-amber-700'}`}>
                    {r.drift > 0 ? '+' : ''}{fmt(r.drift)}
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
